/**
 * LT-16 Part 3d — Versions tab.
 *
 * Lists sent versions only. Voided rows live in the audit trail; the
 * version list exists to answer "what does the client have". Each
 * entry opens its stored PDF from GridFS via
 * GET /api/tenders/{id}/versions/{ref}/pdf.
 *
 * Version numbering is V1, V2, V3 — whole numbers only (LT-16 Part
 * 3c). Post-send corrections are the next version, never a lettered
 * patch of a version the client is already holding.
 */
import React, { useEffect, useState } from "react";
import { Download, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, EmptyState } from "../../components/UI";
import { gbp, ukDate } from "../../lib/format";

export function VersionsTab({ tender }) {
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/tenders/${tender.id}/versions`)
      .then((r) => setVersions(r.data?.versions || []))
      .catch(() => toast.error("Failed to load versions"))
      .finally(() => setLoading(false));
  }, [tender.id]);

  const download = async (ref) => {
    try {
      // Use axios blob download — respects the auth interceptor.
      const r = await api.get(
        `/tenders/${tender.id}/versions/${ref}/pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tender.ref || tender.id}_${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  if (loading) {
    return <div className="mt-6 text-ink-3" style={{ fontSize: 12 }}>Loading versions…</div>;
  }

  return (
    <div className="mt-6" data-testid="versions-tab">
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 15, fontWeight: 600 }}>
          Version history
        </h3>
        <p className="text-ink-3 mt-1 mb-4 flex items-start gap-1.5 max-w-2xl" style={{ fontSize: 12 }}>
          <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
          <span>
            Sent versions only. A voided or unsent version doesn't appear here — it lives in the
            audit trail. Every row opens the exact PDF the client received, not a re-render.
          </span>
        </p>

        {(!versions || versions.length === 0) ? (
          <EmptyState
            title="No versions issued yet"
            hint="Go to Adjudication and Create Client Estimate to generate the first version, then Mark as Sent when you've emailed it."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-ink-3 border-b border-line-soft" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                <th className="text-left py-2">Version</th>
                <th className="text-left py-2">Sent</th>
                <th className="text-right py-2">Client total</th>
                <th className="text-left py-2 pl-4">Reason / notes</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-b border-line-soft" data-testid={`version-row-${v.version_ref}`}>
                  <td className="py-2.5 font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                    {v.version_ref}
                  </td>
                  <td className="py-2.5 font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{ukDate(v.sent_at)}</td>
                  <td className="py-2.5 font-mono tnum text-ink text-right" style={{ fontSize: 13 }}>{gbp(v.client_total)}</td>
                  <td className="py-2.5 pl-4 text-ink-2" style={{ fontSize: 12 }}>
                    {v.bypass_reason || (v.bypassed_blocker_codes?.length ? `Bypassed ${v.bypassed_blocker_codes.length} blocker(s)` : "—")}
                  </td>
                  <td className="py-2.5 text-right">
                    <button
                      onClick={() => download(v.version_ref)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2"
                      style={{ fontSize: 12 }}
                      data-testid={`version-download-${v.version_ref}`}
                    >
                      <Download size={12} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
